import { AxiosError, AxiosResponse } from "axios"
import { UseInfiniteQueryOptions } from "react-query"

import { QueryOptions } from "../../api.types"
import { IExportTaskResponse } from "../queries/export.types"

type TypeIn =
  | "salary"
  | "expense"
  | "bonus"
  | "fine"
  | "purchase"
  | "refund"
  | "reset"
  | "transfer"
  | "interest"

type SenderType = "teacher" | "helper" | ""

type SortBy = "first_name" | "last_name"

type Sorting = "asc" | "desc"

export type ItemVariants = "bonus" | "fine"

interface Classes {
  id: number
  name: string
}

interface Teachers {
  id: number
  first_name: string
  last_name: string
  email: string
}

interface Helpers {
  id: number
  first_name: string
  last_name: string
  login: string
}

interface Students {
  id: number
  first_name: string
  last_name: string
  login: string
}

export interface TeacherAdminSchoolTransactionsSchoolDataResponse {
  classes: Classes[]
  teachers: Teachers[]
  helpers: Helpers[]
  students: Students[]
}

export interface TeacherAdminSchoolTransactionsResponse {
  id: number
  title: string
  type: TypeIn
  class_: {
    id: number
    name: string
  }
  class_name: string
  teacher: {
    id: number
    first_name: string
    last_name: string
  }
  helper: {
    id: number
    first_name: string
    last_name: string
  }
  student: {
    first_name: string
    last_name: string
    login: string
    id: number
  }
  created_at: Date
  amount: number
  balance_after: number
  is_booked: boolean
}

export interface TeacherAdminSchoolTransactionsInfinity {
  students: number[]
  date_from: string
  date_to: string
  classes: number[]
  student_helpers: number[]
  teachers: number[]
  type_in: TypeIn[]
  title: string
  sender_type: SenderType
  skip: number
  limit: number
  options: Omit<
    UseInfiniteQueryOptions<
      AxiosResponse<TeacherAdminSchoolTransactionsResponse[]>,
      AxiosError
    >,
    "queryKey" | "queryFn"
  >
}

export interface TeachersManagement {
  id: number
  first_name: string
  last_name: string
  email: string
  avatar_url: string | null
  fk_school_account_id: number
  is_school_admin: boolean
}

export interface BonusesAdminTypes {
  title: string
  amount: number
  id: number
  is_preset: boolean
}

export interface ExportAdminTransactionsArgs {
  students?: number[]
  date_from?: string
  date_to?: string
  classes?: number[]
  student_helpers?: number[]
  teachers?: number[]
  type_in?: TypeIn[]
  title?: string
  sender_type?: SenderType
  options?: QueryOptions<IExportTaskResponse>
}

export interface BonusesAdminArgs {
  variant: ItemVariants
  options: QueryOptions<BonusesAdminTypes[]>
}

export interface TeachersManagementArgs {
  filter?: string
  skip: number
  limit: number
  sort_by?: SortBy
  sorting?: Sorting
  options: Omit<
    UseInfiniteQueryOptions<AxiosResponse<TeachersManagement[]>, AxiosError>,
    "queryKey" | "queryFn"
  >
}

export interface TeacherAdminSchoolTransactionsSchoolDataArgs {
  options: QueryOptions<TeacherAdminSchoolTransactionsSchoolDataResponse>
}

export interface ISchoolTransactionsIdsArgs {
  params: Partial<
    Omit<TeacherAdminSchoolTransactionsInfinity, "skip" | "limit" | "options">
  >
  options?: QueryOptions<number[]>
}

export interface TeacherAdminSchoolTransactionsInfinityArgs
  extends Partial<
    Omit<TeacherAdminSchoolTransactionsInfinity, "skip" | "limit">
  > {
  skip: number
  limit: number
}

export enum AttendanceLevel {
  School = "school",
  Section = "section",
}

export interface IAttendanceSettings {
  has_attendance_integration: boolean
  attendance_bonus_amount: string | null
  attendance_level: AttendanceLevel
}

export interface AttendanceSettingsArgs {
  options?: QueryOptions<IAttendanceSettings>
}
